import { useApolloClient } from '@apollo/client'
import { ExternalLink, Loader } from 'lucide-react'
import { useTransition } from 'react'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Button, buttonVariants } from '@/components/ui/button'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany } from '@/lib/hooks'
import type { VariantProps } from 'class-variance-authority'

const STRIPE_CUSTOMER_PORTAL_QUERY = gql`
  query StripeCustomerPortal($idCompany: String!) {
    company(id: $idCompany) {
      id
      sevallaBilling {
        id
        stripeCustomerPortal
      }
    }
  }
`

export const ManageBillingButton = ({
  variant = 'default',
  className,
  label = 'Manage billing',
}: {
  variant?: VariantProps<typeof buttonVariants>['variant']
  className?: string
  label?: string
}) => {
  const client = useApolloClient()
  const { idCompany } = useIdCompany()
  const [isPending, startTransition] = useTransition()

  const handleClick = () => {
    startTransition(async () => {
      try {
        const { data } = await client.query({
          query: STRIPE_CUSTOMER_PORTAL_QUERY,
          variables: { idCompany },
          fetchPolicy: 'network-only',
        })

        const portalUrl = data?.company?.sevallaBilling?.stripeCustomerPortal

        if (portalUrl) {
          window.open(portalUrl, '_blank', 'noopener,noreferrer')
        } else {
          toast(
            <ToasterContent variant="error">
              Unable to open billing portal
            </ToasterContent>,
            { position: 'top-right' }
          )
        }
      } catch {
        toast(
          <ToasterContent variant="error">
            Unable to open billing portal
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <Button
      variant={variant}
      className={className}
      disabled={isPending}
      onClick={handleClick}
    >
      {isPending ? (
        <Loader className="animate-spin size-4" />
      ) : (
        <ExternalLink className="size-4" />
      )}
      {label}
    </Button>
  )
}
